import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Plus, Trash, Wallet, ArrowLeft, CheckCircle, XCircle, Package, Truck, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductAiSpecs from '../components/ProductAiSpecs';
import CompareToggle from '../components/CompareToggle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_ORIGIN = 'https://servermemoryrfq.com';
const RFQ_STORAGE_KEY = 'rfq_parts_v7';
const RECENT_STORAGE_KEY = 'sm_recently_viewed_v1';
const RECENT_MAX = 10;

const loadParts = () => {
  try { const s = localStorage.getItem(RFQ_STORAGE_KEY); return s ? JSON.parse(s) : []; }
  catch(e) { return []; }
};
const saveParts = (parts) => {
  try { localStorage.setItem(RFQ_STORAGE_KEY, JSON.stringify(parts)); } catch(e) { /* ignore */ }
};

const loadRecent = () => {
  try { const s = localStorage.getItem(RECENT_STORAGE_KEY); return s ? JSON.parse(s) : []; }
  catch(e) { return []; }
};
const pushRecent = (product) => {
  if (!product || !product.slug) return;
  try {
    const existing = loadRecent().filter(p => p.slug !== product.slug);
    const next = [{
      slug: product.slug,
      name: product.name,
      description: product.description,
      condition: product.condition,
      taa: product.taa,
      qty_on_hand: product.qty_on_hand,
      viewed_at: Date.now(),
    }, ...existing].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
  } catch(e) { /* ignore */ }
};

/**
 * Injects (or updates) a <meta> or <link> tag by attribute selector.
 * Idempotent — safe to call on every product render.
 */
const upsertHead = (tag, attrs) => {
  const selector = Object.entries(attrs)
    .filter(([k]) => k !== 'content' && k !== 'href')
    .map(([k, v]) => `[${k}="${v}"]`).join('');
  let el = document.head.querySelector(`${tag}${selector}`);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [rfqParts, setRfqParts] = useState(() => loadParts());
  const [related, setRelated] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setRelated([]);
    window.scrollTo(0, 0);
    axios.get(`${API_URL}/api/inventory/slug/${slug}`)
      .then(res => {
        if (cancelled) return;
        if (res.data.redirect_to) { navigate(`/product/${res.data.redirect_to}`, { replace: true }); return; }
        setProduct(res.data); setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        if (err.response && err.response.status === 404) setNotFound(true);
        setLoading(false);
      });
    axios.get(`${API_URL}/api/inventory/slug/${slug}/related?limit=8`)
      .then(res => { if (!cancelled) setRelated(res.data.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug, navigate]);

  // Inject SEO meta tags + JSON-LD Product structured data every time product loads.
  useEffect(() => {
    if (!product) return;
    // Track this view + refresh the "Recently viewed" strip (exclude current).
    pushRecent(product);
    setRecent(loadRecent().filter(p => p.slug !== product.slug));

    const url = `${SITE_ORIGIN}/product/${product.slug}`;
    const title = `${product.name} — ${product.description} | ServerMemoryRFQ`;
    const desc = product.ai_specs?.summary ? `${product.ai_specs.summary} ${product.qty_on_hand} in stock at ServerMemoryRFQ.` : `${product.name} — ${product.description}. Condition: ${product.condition || 'New'}${product.taa === 'Yes' ? ', TAA Compliant' : ''}. ${product.qty_on_hand} in stock, ready to ship from ServerMemoryRFQ. Request a free B2B quote for enterprise server memory.`;

    document.title = title;
    upsertHead('meta', { name: 'description', content: desc });
    upsertHead('link', { rel: 'canonical', href: url });
    upsertHead('meta', { property: 'og:type', content: 'product' });
    upsertHead('meta', { property: 'og:title', content: title });
    upsertHead('meta', { property: 'og:description', content: desc });
    upsertHead('meta', { property: 'og:url', content: url });
    upsertHead('meta', { name: 'twitter:card', content: 'summary_large_image' });
    upsertHead('meta', { name: 'twitter:title', content: title });
    upsertHead('meta', { name: 'twitter:description', content: desc });

    upsertJsonLd('ld-product', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': url,
      name: product.name,
      sku: product.name,
      mpn: product.name,
      description: product.description,
      url,
      brand: { '@type': 'Brand', name: 'ServerMemoryRFQ' },
      category: 'Server Memory / Enterprise RAM',
      itemCondition: product.condition === 'REF' ? 'https://schema.org/RefurbishedCondition' : 'https://schema.org/NewCondition',
      offers: {
        '@type': 'Offer',
        url,
        priceCurrency: 'USD',
        availability: product.qty_on_hand > 0 ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
        priceSpecification: {
          '@type': 'PriceSpecification',
          description: 'Request quote or connect Web3 wallet for live pricing',
        },
        seller: { '@type': 'Organization', name: 'ServerMemoryRFQ', url: SITE_ORIGIN },
      },
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'TAA Compliant', value: product.taa === 'Yes' ? 'Yes' : 'No' },
        { '@type': 'PropertyValue', name: 'Condition', value: product.condition || 'New' },
        { '@type': 'PropertyValue', name: 'Quantity On Hand', value: String(product.qty_on_hand) },
        { '@type': 'PropertyValue', name: 'Incoming Quantity', value: String(product.qty_incoming) },
        ...(product.ai_specs?.specs || []).map(s => ({ '@type': 'PropertyValue', name: s.label, value: s.value })),
      ],
    });

    if (product.ai_specs?.faq?.length) {
      upsertJsonLd('ld-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: product.ai_specs.faq.map(f => ({
          '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    } else {
      document.getElementById('ld-faq')?.remove();
    }

    upsertJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Inventory', item: `${SITE_ORIGIN}/inventory` },
        { '@type': 'ListItem', position: 3, name: product.name, item: url },
      ],
    });
  }, [product]);

  useEffect(() => { saveParts(rfqParts); }, [rfqParts]);

  const isInRfq = product && rfqParts.some(p => p.name === product.name);

  const handleAddToRfq = () => {
    if (!product) return;
    if (isInRfq) { toast.error('Already in your RFQ'); return; }
    const newPart = {
      name: product.name,
      description: product.description,
      condition: product.condition,
      taa: product.taa,
      qty_on_hand: product.qty_on_hand,
      requested_qty: qty,
      available_qty_at_selection: product.qty_on_hand,
      selected_at: new Date().toISOString(),
    };
    setRfqParts([...rfqParts, newPart]);
    toast.success(`Added ${product.name} × ${qty} to RFQ`);
  };

  const handleRemoveFromRfq = () => {
    if (!product) return;
    setRfqParts(rfqParts.filter(p => p.name !== product.name));
    toast.info('Removed from RFQ');
  };

  if (loading) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-5xl mx-auto px-4 py-24 text-center text-[#94A3B8] font-mono">Loading product…</div>
        <Footer />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-5xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
          <XCircle size={64} className="mx-auto text-[#94A3B8] mb-6" />
          <h1 className="text-3xl font-['JetBrains_Mono'] font-bold mb-4">Product not found</h1>
          <p className="text-[#94A3B8] mb-8">The part <code className="text-cyan-400">{slug}</code> is not in our live inventory. It may have sold out or been discontinued.</p>
          <Button data-testid="browse-inventory-cta" onClick={() => navigate('/inventory')} className="bg-[#0047AB] hover:bg-[#005DF0] font-mono">Browse full inventory</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const inStock = product.qty_on_hand > 0;

  return (
    <div className="min-h-screen" data-testid="product-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm font-mono text-[#94A3B8]" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#007AFF]">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/inventory" className="hover:text-[#007AFF]">Inventory</Link>
          <span className="mx-2">/</span>
          {product.category && (
            <>
              <Link to={`/category/${product.category.slug}`} className="hover:text-[#007AFF]" data-testid="breadcrumb-category">{product.category.title.replace(' Server Memory', '')}</Link>
              <span className="mx-2">/</span>
            </>
          )}
          <span className="text-white">{product.name}</span>
        </nav>

        {/* Back link */}
        <Link to="/inventory" className="inline-flex items-center gap-2 text-[#007AFF] hover:text-[#005DF0] mb-6 font-mono text-sm" data-testid="back-to-inventory">
          <ArrowLeft size={16} /> Back to inventory
        </Link>

        {/* Product Header */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-8 mb-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="ml-auto order-last"><CompareToggle item={product} testId="product-compare-toggle" /></span>
            {inStock ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs font-mono uppercase tracking-wider" data-testid="stock-badge-in">
                <CheckCircle size={14} weight="fill" /> In Stock — {product.qty_on_hand} available
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-mono uppercase tracking-wider" data-testid="stock-badge-out">
                <Package size={14} weight="fill" /> Pre-order — Incoming
              </span>
            )}
            {product.taa === 'Yes' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#007AFF]/20 text-[#007AFF] text-xs font-mono uppercase tracking-wider" data-testid="taa-badge">
                <ShieldCheck size={14} weight="fill" /> TAA Compliant
              </span>
            )}
            {product.condition && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#334155] text-[#94A3B8] text-xs font-mono uppercase tracking-wider" data-testid="condition-badge">
                {product.condition === 'F/S' ? 'Factory Sealed' : product.condition === 'REF' ? 'Refurbished' : product.condition}
              </span>
            )}
          </div>

          <h1 className="text-4xl lg:text-5xl font-['JetBrains_Mono'] font-bold text-cyan-400 mb-2" data-testid="product-name">
            {product.name}
          </h1>
          <p className="text-xl text-white/90 mb-6" data-testid="product-description">
            {product.description}
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-[#334155]">
            <div>
              <div className="text-xs font-mono uppercase text-[#94A3B8] mb-1">On Hand</div>
              <div className="text-2xl font-mono font-bold text-[#10B981]" data-testid="qty-on-hand">{product.qty_on_hand}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase text-[#94A3B8] mb-1">Incoming</div>
              <div className="text-2xl font-mono font-bold text-[#F59E0B]" data-testid="qty-incoming">{product.qty_incoming}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase text-[#94A3B8] mb-1">Condition</div>
              <div className="text-lg font-mono text-[#007AFF]">{product.condition || 'New'}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase text-[#94A3B8] mb-1">TAA</div>
              <div className="text-lg font-mono">{product.taa === 'Yes' ? <span className="text-[#10B981]">Yes</span> : <span className="text-[#94A3B8]">No</span>}</div>
            </div>
          </div>
        </div>

        {/* Pricing Gate — no price shown publicly */}
        <div className="bg-gradient-to-br from-[#0047AB]/20 to-[#9945FF]/10 border border-[#0047AB]/40 rounded-lg p-8 mb-6" data-testid="pricing-gate">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl font-['JetBrains_Mono'] font-bold text-white mb-3">Get Live Pricing</h2>
            <p className="text-[#94A3B8] mb-6">
              Prices are quoted on request or revealed instantly to connected Web3 wallets. Choose your path:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Path 1: Traditional RFQ */}
              <div className="bg-[#05050A]/60 border border-[#334155] rounded-lg p-6">
                <Truck size={32} className="text-[#10B981] mx-auto mb-3" weight="duotone" />
                <div className="font-mono font-bold text-white mb-2">Traditional Quote</div>
                <p className="text-sm text-[#94A3B8] mb-4">Add to your RFQ, submit contact info, get a formal quote via email within hours.</p>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <label className="text-xs font-mono uppercase text-[#94A3B8]">Qty:</label>
                  <Input
                    type="number" min="1"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                    className="bg-[#05050A] border-[#334155] w-20 h-10 text-center font-mono"
                    data-testid="qty-input"
                    disabled={isInRfq}
                  />
                </div>
                {isInRfq ? (
                  <Button onClick={handleRemoveFromRfq} className="w-full bg-red-600 hover:bg-red-700 font-mono" data-testid="remove-from-rfq-btn">
                    <Trash size={18} className="mr-2" /> Remove from RFQ
                  </Button>
                ) : (
                  <Button onClick={handleAddToRfq} className="w-full bg-[#10B981] hover:bg-[#059669] font-mono font-bold" data-testid="add-to-rfq-btn">
                    <Plus size={18} className="mr-2" /> Add to RFQ
                  </Button>
                )}
              </div>

              {/* Path 2: Web3 wallet */}
              <div className="bg-[#05050A]/60 border border-[#9945FF]/40 rounded-lg p-6">
                <Wallet size={32} className="text-[#9945FF] mx-auto mb-3" weight="duotone" />
                <div className="font-mono font-bold text-white mb-2">Web3 Instant Price</div>
                <p className="text-sm text-[#94A3B8] mb-4">Connect MetaMask or Coinbase Wallet to see live USD price and pay with USDC/ETH on Base or Ethereum.</p>
                <div className="flex items-center justify-center gap-1 text-xs font-mono text-[#94A3B8] mb-3 h-10 items-center">
                  <ShieldCheck size={14} className="text-[#10B981]" /> On-chain escrow · No account
                </div>
                <Button
                  onClick={() => navigate(`/agents?part=${encodeURIComponent(product.name)}`)}
                  className="w-full bg-gradient-to-r from-[#9945FF] to-[#14F195] hover:opacity-90 font-mono font-bold text-white"
                  data-testid="connect-wallet-btn"
                >
                  <Wallet size={18} className="mr-2" /> Connect Wallet
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* RFQ Cart pill */}
        {rfqParts.length > 0 && (
          <div className="bg-[#10B981] rounded-lg p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="rfq-cart-pill">
            <div className="flex items-center gap-3">
              <ShoppingCart size={24} className="text-white" weight="fill" />
              <span className="font-mono font-bold text-white">
                {rfqParts.length} part(s) in your RFQ
              </span>
            </div>
            <Button onClick={() => navigate('/rfq')} className="bg-white text-[#10B981] hover:bg-gray-100 font-bold" data-testid="go-to-rfq-btn">
              Continue to RFQ Form →
            </Button>
          </div>
        )}

        <ProductAiSpecs specs={product.ai_specs} status={product.ai_specs_status} name={product.name} />

        {/* Product details / SEO body */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 mb-6">
          <h2 className="text-lg font-['JetBrains_Mono'] font-bold text-white mb-3">About {product.name}</h2>
          <div className="prose prose-invert max-w-none text-[#94A3B8] text-sm">
            <p>
              The <strong className="text-white">{product.name}</strong> is a
              {product.taa === 'Yes' ? ' TAA-compliant ' : ' '}
              enterprise server memory module ({product.description}). Sold as
              {product.condition === 'F/S' ? ' factory-sealed OEM stock ' : product.condition === 'REF' ? ' certified refurbished ' : ` ${product.condition || 'new'} `}
              with a 1-year warranty and same-day US shipping when in stock.
              We stock <strong className="text-white">{product.qty_on_hand}</strong> units of this SKU today
              {product.qty_incoming > 0 ? <>, with <strong className="text-white">{product.qty_incoming}</strong> more incoming</> : null}.
            </p>
            <p className="mt-3">
              Compatible with enterprise servers requiring this exact configuration.
              Bulk orders welcome — quotes typically returned within one business hour.
              Payment terms: NET-30 for approved accounts, or instant on-chain payment
              via USDC/ETH on Base or Ethereum for autonomous AI agents and Web3 buyers.
            </p>
          </div>
        </div>

        {/* You may also like — related products (SEO + conversion booster) */}
        {related.length > 0 && (
          <section className="mb-8" data-testid="related-products">
            <h2 className="text-2xl font-['JetBrains_Mono'] font-bold text-white mb-1">You may also like</h2>
            <p className="text-sm text-[#94A3B8] mb-5">Similar server memory modules in our live inventory</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r) => {
                const rSlug = r.slug || r.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                const rQty = parseInt(r.qty_on_hand) || 0;
                return (
                  <Link
                    key={rSlug}
                    to={`/product/${rSlug}`}
                    className="block bg-[#11141D] border border-[#1E293B] hover:border-[#007AFF] rounded-lg p-4 transition-colors group"
                    data-testid={`related-card-${rSlug}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      {rQty > 0 ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded">
                          In Stock · {rQty}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-0.5 rounded">
                          Pre-order
                        </span>
                      )}
                      {r.taa === 'Yes' && (
                        <span className="text-[10px] font-mono uppercase text-[#007AFF]">TAA</span>
                      )}
                    </div>
                    <div className="font-mono text-cyan-400 group-hover:text-cyan-300 font-bold text-sm mb-1 break-all">
                      {r.name}
                    </div>
                    <div className="text-[#94A3B8] text-xs line-clamp-2 min-h-[2.5rem]">
                      {r.description}
                    </div>
                    <div className="mt-3 text-xs font-mono text-[#007AFF] group-hover:text-[#005DF0]">
                      View details →
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Recently viewed — client-side trail (localStorage). SEO bonus + stickiness. */}
        {recent.length > 0 && (
          <section className="mb-8" data-testid="recently-viewed">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <h2 className="text-2xl font-['JetBrains_Mono'] font-bold text-white">Recently viewed</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const existingNames = new Set(rfqParts.map(p => p.name));
                    const toAdd = recent.filter(r => !existingNames.has(r.name));
                    if (toAdd.length === 0) {
                      toast.info('All recently viewed parts are already in your RFQ');
                      return;
                    }
                    const newParts = toAdd.map(r => ({
                      name: r.name,
                      description: r.description,
                      condition: r.condition,
                      taa: r.taa,
                      qty_on_hand: r.qty_on_hand,
                      requested_qty: 1,
                      available_qty_at_selection: r.qty_on_hand,
                      selected_at: new Date().toISOString(),
                    }));
                    const merged = [...rfqParts, ...newParts];
                    setRfqParts(merged);
                    saveParts(merged);
                    toast.success(`Added ${toAdd.length} part(s) to RFQ`);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-mono font-bold uppercase tracking-wider bg-[#10B981] hover:bg-[#059669] text-white px-3 py-1.5 rounded transition-colors"
                  data-testid="add-all-recently-viewed-btn"
                >
                  <Plus size={14} weight="bold" /> Add all to RFQ
                </button>
                <button
                  onClick={() => { localStorage.removeItem(RECENT_STORAGE_KEY); setRecent([]); }}
                  className="text-xs font-mono text-[#94A3B8] hover:text-white transition-colors"
                  data-testid="clear-recently-viewed-btn"
                >
                  Clear
                </button>
              </div>
            </div>
            <p className="text-sm text-[#94A3B8] mb-5">Parts you looked at earlier in this session</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {recent.slice(0, 6).map((r) => {
                const rQty = parseInt(r.qty_on_hand) || 0;
                return (
                  <Link
                    key={r.slug}
                    to={`/product/${r.slug}`}
                    className="block bg-[#11141D] border border-[#1E293B] hover:border-[#94A3B8] rounded-lg p-3 transition-colors group"
                    data-testid={`recent-card-${r.slug}`}
                  >
                    {rQty > 0 ? (
                      <span className="text-[9px] font-mono uppercase tracking-wider text-[#10B981]">● In stock</span>
                    ) : (
                      <span className="text-[9px] font-mono uppercase tracking-wider text-[#F59E0B]">● Pre-order</span>
                    )}
                    <div className="font-mono text-cyan-400 group-hover:text-cyan-300 font-bold text-xs mt-1 mb-1 break-all line-clamp-1">
                      {r.name}
                    </div>
                    <div className="text-[#94A3B8] text-[11px] line-clamp-2 min-h-[2rem]">
                      {r.description}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
      <div className="sticky bottom-0 z-30 bg-[#05050A]/85 backdrop-blur-md border-t border-[#1E293B]" data-testid="sticky-rfq-bar">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="font-mono font-bold text-cyan-400 text-sm truncate">{product.name}</div>
            <div className="text-[11px] text-[#94A3B8] truncate">{product.description}</div>
          </div>
          <Input
            type="number" min="1" value={qty}
            onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
            className="bg-[#05050A] border-[#334155] w-20 h-9 text-center font-mono"
            data-testid="sticky-qty-input" disabled={isInRfq}
          />
          {isInRfq ? (
            <Button onClick={() => navigate('/rfq')} className="bg-white text-[#10B981] hover:bg-gray-100 font-mono font-bold h-9" data-testid="sticky-go-to-rfq-btn">
              <ShoppingCart size={16} className="mr-2" /> View RFQ Cart ({rfqParts.length})
            </Button>
          ) : (
            <Button onClick={handleAddToRfq} className="bg-[#10B981] hover:bg-[#059669] font-mono font-bold h-9" data-testid="sticky-add-to-rfq-btn">
              <Plus size={16} className="mr-2" /> Add to RFQ Cart
            </Button>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductPage;
